import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal, Badge } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { ChatDots, CheckCircle } from "react-bootstrap-icons";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import MessageCard from "../components/MessageCard";
import { whoami, getMessages, createMessage, deleteMessageById } from "../services/APIClient";
import "../components/SchoolAdmin.css";

function MessageEditor() {
  const [teacherid, setTeacherid] = useState(101);
  const [name, setName] = useState("Admin User");

  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ type: "", text: "" });

  // All messages list
  const [allMessages, setAllMessages] = useState([]);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newMessageId, setNewMessageId] = useState("");
  const [newText, setNewText] = useState("");
  const [newActive, setNewActive] = useState(true);
  const [newRoute, setNewRoute] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    whoami().then((response) => {
      setTeacherid(response.data.stakeholder.id);
      setName(response.data.stakeholder.name);
    });
    loadMessage();
  }, []);

  const loadMessage = async () => {
    setLoading(true);
    try {
      const all = await getMessages();
      const messages = Array.isArray(all) ? all : all?.data ?? [];
      setAllMessages(messages);
    } catch (err) {
      console.error("Failed to load messages:", err);
      setAlert({ type: "danger", text: "Failed to load message data" });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMessage = async () => {
    if (!newMessageId.trim() || !newText.trim()) {
      setAlert({ type: "danger", text: "Please fill in all fields." });
      return;
    }
    setCreating(true);
    setAlert({ type: "", text: "" });
    try {
      const isExternal = newRoute.trim().startsWith("http");
      await createMessage({
        message_id: newMessageId.trim(),
        text: newText.trim(),
        active: newActive,
        created_by: name,
        ...(newRoute.trim() && isExternal && { hyperlink: newRoute.trim() }),
        ...(newRoute.trim() && !isExternal && { route: newRoute.trim() }),
      });
      setNewMessageId("");
      setNewText("");
      setNewActive(true);
      setNewRoute("");
      setShowCreateModal(false);
      setAlert({ type: "success", text: "Message created!" });
      setTimeout(() => setAlert({ type: "", text: "" }), 3000);
      await loadMessage();
    } catch (err) {
      console.error("Failed to create message:", err);
      setAlert({ type: "danger", text: "Failed to create message. Please try again." });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    setAlert({ type: "", text: "" });
    try {
      await deleteMessageById(id);
      setAlert({ type: "success", text: "Message deleted!" });
      setTimeout(() => setAlert({ type: "", text: "" }), 3000);
      await loadMessage();
    } catch (err) {
      console.error("Failed to delete message:", err);
      setAlert({ type: "danger", text: "Failed to delete message. Please try again." });
    }
  };

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner name={`Welcome ${name}!`} isLoggedIn={true} cloud={true} teacherid={teacherid} />

        <Row className="class-admin-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

            <div className="class-admin-header">
              <h2 className="class-admin-page-title mb-0">Message Editor</h2>
            </div>

            <div className="text-center mb-3">
              <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                <FaPlus className="me-2" />Create New Message
              </Button>
            </div>

            {alert.text && (
              <Alert variant={alert.type} dismissible onClose={() => setAlert({ type: "", text: "" })}>
                {alert.text}
              </Alert>
            )}

            {loading ? (
              <div className="text-center p-5">
                <Spinner animation="border" />
              </div>
            ) : allMessages.length === 0 ? (
              <Alert variant="info">No messages found.</Alert>
            ) : (
              allMessages.map((msg) => (
                <MessageCard
                  key={msg.id}
                  message={msg}
                  updatedBy={name}
                  onDelete={handleDeleteMessage}
                  onSaved={() => {
                    setAlert({ type: "success", text: "Message saved!" });
                    setTimeout(() => setAlert({ type: "", text: "" }), 3000);
                  }}
                />
              ))
            )}

          </Col>
        </Row>
      </Container>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <ChatDots className="me-2" />
            Create New Message
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {alert.text && (
            <Alert variant={alert.type} dismissible onClose={() => setAlert({ type: "", text: "" })}>
              {alert.text}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Message ID</Form.Label>
            <Form.Control
              value={newMessageId}
              onChange={(e) => setNewMessageId(e.target.value)}
              placeholder="e.g. home_message"
              size="lg"
              style={{ borderRadius: '8px' }} />
            <Form.Text className="text-muted">A unique identifier for this message</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder="Enter your message text"
              style={{ borderRadius: '8px' }} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Link / Route (optional)</Form.Label>
            <Form.Control
              value={newRoute}
              onChange={(e) => setNewRoute(e.target.value)}
              placeholder="https://example.com or /beforescreening"
              style={{ borderRadius: '8px' }} />
          </Form.Group>

          <Form.Group className="mb-3">
            <div className="d-flex align-items-center">
              <Form.Check
                type="switch"
                checked={newActive}
                onChange={(e) => setNewActive(e.target.checked)} />
              <Badge bg={newActive ? "success" : "secondary"} className="ms-2">
                {newActive ? "Active" : "Inactive"}
              </Badge>
            </div>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreateMessage} disabled={creating}>
            {creating ? "Creating..." : <><CheckCircle className="me-2" />Create Message</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MessageEditor;
